import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CompraComentarioService } from '@shared/services/apis/compras/compra-comentario.service';
import type { Person } from '../../../processo-compras.models';
import { avatarColorFromSeed, CardComment } from '../card-edit-dialog.types';

/** Painel de comentários do card (lista + composer com menção via @). */
@Component({
  selector: 'app-ced-comentarios',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './ced-comentarios.component.html',
  styleUrls: ['../ced-scrollbars.css', './ced-comentarios.component.css'],
})
export class CedComentariosComponent {
  @Input() idCompra?: number | null;
  @Input() comments: CardComment[] = [];
  @Input() peopleList: { key: string; name: string; color: string; idUsuario: number }[] = [];
  @Input() currentPerson?: Person;

  /** Emitido quando a lista de comentários muda — o shell é o dono do array. */
  @Output() commentsChange = new EventEmitter<CardComment[]>();

  @ViewChild('commentInput') commentInput?: ElementRef<HTMLTextAreaElement>;

  draft = '';
  mention: { query: string; start: number } | null = null;
  postingComment = false;

  constructor(
    private compraComentarioService: CompraComentarioService,
    private alertService: AlertService,
  ) {}

  getPerson(key: string) {
    const person = this.peopleList.find(p => p.key === key);
    return {
      name: person?.name || key || 'Sem responsável',
      color: person?.color || avatarColorFromSeed(key),
    };
  }

  /** Splits a comment into plain-text and @mention segments for rendering. */
  commentParts(text: string): { type: 'text' | 'mention'; value: string; color?: string }[] {
    if (!text) return [];
    const people = this.peopleList.slice().sort((a, b) => b.name.length - a.name.length);
    const parts: { type: 'text' | 'mention'; value: string; color?: string }[] = [];
    let i = 0;
    while (i < text.length) {
      if (text[i] === '@') {
        const rest = text.slice(i + 1);
        const hit = people.find(p => rest.startsWith(p.name));
        if (hit) {
          parts.push({ type: 'mention', value: hit.name, color: hit.color });
          i += 1 + hit.name.length;
          continue;
        }
      }
      let j = text.indexOf('@', i);
      if (j === -1) j = text.length;
      if (j > i) {
        parts.push({ type: 'text', value: text.slice(i, j) });
        i = j;
      } else {
        parts.push({ type: 'text', value: text[i] });
        i += 1;
      }
    }
    return parts;
  }

  onDraftChange(event: Event): void {
    const ta = event.target as HTMLTextAreaElement;
    this.draft = ta.value;
    const caret = ta.selectionStart || 0;
    const before = this.draft.slice(0, caret);
    const m = before.match(/(?:^|\s)@([A-Za-zÀ-ÿ]*)$/);
    this.mention = m ? { query: m[1], start: caret - m[1].length - 1 } : null;
  }

  get mentionSuggestions() {
    if (!this.mention) return [];
    const q = this.mention.query.toLowerCase();
    return this.peopleList.filter(p => p.name.toLowerCase().includes(q)).slice(0, 5);
  }

  insertMention(person: { key: string; name: string; color: string }): void {
    if (!this.mention) return;
    const ta = this.commentInput?.nativeElement;
    const caret = ta?.selectionStart ?? this.mention.start + 1 + this.mention.query.length;
    const before = this.draft.slice(0, this.mention.start);
    const after = this.draft.slice(caret);
    this.draft = `${before}@${person.name} ${after}`;
    this.mention = null;
    setTimeout(() => {
      if (ta) {
        const pos = (before + '@' + person.name + ' ').length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      }
    });
  }

  postComment(): void {
    const text = this.draft.trim();
    if (!text || !this.idCompra || this.postingComment) return;
    // Mesma regra de commentParts: menção é "@" + nome exato de alguém da lista.
    const idsUsuarioMencionado = [...new Set(
      this.commentParts(text)
        .filter(part => part.type === 'mention')
        .map(part => this.peopleList.find(p => p.name === part.value)?.idUsuario)
        .filter((id): id is number => !!id),
    )];
    this.postingComment = true;
    this.compraComentarioService.adicionarComentario(this.idCompra, text, idsUsuarioMencionado).subscribe({
      next: (res) => {
        this.postingComment = false;
        if (!res.isError) {
          this.comments = [...this.comments, {
            who: this.currentPerson?.initials || '',
            whoName: this.currentPerson?.name || 'Você',
            whoColor: this.currentPerson?.color || avatarColorFromSeed(this.currentPerson?.initials || ''),
            when: new Date().toLocaleDateString('pt-BR'),
            text,
          }];
          this.draft = '';
          this.mention = null;
          this.commentsChange.emit(this.comments);
        } else {
          this.alertService.show('Erro', res.errorDescription || 'Não foi possível enviar o comentário.', AlertType.error);
        }
      },
      error: () => {
        this.postingComment = false;
        this.alertService.show('Erro', 'Não foi possível enviar o comentário.', AlertType.error);
      },
    });
  }
}
